import { isNil, toNumber } from 'lodash'

import { TableApi, TransformedPageData } from '../types/crud'
import { GenericalRecord, StrictOmit } from '../types/utilities'

import { ApiConfig } from './fetcher/interface'

export const pageNoop = (...args: unknown[]) => {
  return Promise.resolve({
    current: 1,
    total: 0,
    list: []
  })
}

type Options<T, P> = {
  requestParams?: P
  format?: (data: T[]) => T[]
}

const DEFAULT_DATA: TransformedPageData<any> = {
  list: [],
  current: 1,
  size: 0,
  pages: 0,
  total: 0,
  apiType: 'page'
}

const DEFAULT_PAGINATION = {
  current: 1,
  pageSize: 10
}

export const toPageApi = <T, P>(
  api: TableApi<T, P>,
  options?: Options<T, P>
) => {
  const { format, requestParams } = options || {}
  return async (
    params: P,
    context?: ApiConfig
  ): Promise<TransformedPageData<T>> => {
    const res = await api(
      {
        ...DEFAULT_PAGINATION,
        ...requestParams,
        ...params
      } as any,
      {
        silence: true,
        ...context
      }
    )
    const data: TransformedPageData<T> = isNil(res)
      ? DEFAULT_DATA
      : Array.isArray(res)
        ? {
            list: res,
            total: res.length,
            size: res.length,
            pages: 1,
            current: 1,
            apiType: 'list'
          }
        : {
            list: res.records || DEFAULT_DATA.list,
            total: toNumber(res.total) || 0,
            pages: toNumber(res.pages) || 0,
            current: toNumber(res.current) || 1,
            size: toNumber(res.size) || 0,
            apiType: 'page'
          }

    if (format) {
      data.list = format(data.list)
    }

    return data
  }
}

export const toPaginationApi = <T, P extends GenericalRecord | never>(
  ...args: Parameters<typeof toPageApi<T, P>>
) => {
  return (
    params: StrictOmit<P, 'current' | 'pageSize'> & {
      current?: number
      pageSize?: number
    },
    context?: ApiConfig
  ) => {
    return toPageApi(...args)(params as any, context)
  }
}
